/**
 * AudioContext - React Context Provider for the audio system
 * Wraps AudioManager and provides React-friendly API
 */

import { createContext, useEffect, useState, useCallback, useMemo } from 'react';
import { audioManager } from './AudioManager.js';

export const AudioContext = createContext(null);

export function AudioProvider({ children }) {
  const [volumes, setVolumes] = useState(audioManager.volumes);
  const [muted, setMutedState] = useState(audioManager.muted);
  const [initialized, setInitialized] = useState(false);

  // Initialize audio on mount
  useEffect(() => {
    const init = async () => {
      await audioManager.initialize();
      setInitialized(true);
      setVolumes({ ...audioManager.volumes });
    };

    // Initialize on first user interaction
    const handleInteraction = () => {
      if (!initialized) {
        init();
        document.removeEventListener('click', handleInteraction);
        document.removeEventListener('keydown', handleInteraction);
      }
    };

    document.addEventListener('click', handleInteraction);
    document.addEventListener('keydown', handleInteraction);

    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
      audioManager.cleanup();
    };
  }, [initialized]);

  // Play a sound by ID
  const playSound = useCallback(async (soundId, options) => {
    if (!audioManager.initialized) {
      await audioManager.initialize();
      setInitialized(true);
    }
    return audioManager.playSound(soundId, options);
  }, []);

  // Play multiple dice sounds with staggering
  const playDice = useCallback(async (diceType, count) => {
    if (!audioManager.initialized) {
      await audioManager.initialize();
      setInitialized(true);
    }
    return audioManager.playDice(diceType, count);
  }, []);

  // Set ambience track with crossfading
  const setAmbience = useCallback(async (trackId, duration) => {
    if (!audioManager.initialized) {
      await audioManager.initialize();
      setInitialized(true);
    }
    return audioManager.setAmbience(trackId, duration);
  }, []);

  // Set volume for a category
  const setVolume = useCallback((category, value) => {
    audioManager.setVolume(category, value);
    setVolumes({ ...audioManager.volumes });
  }, []);

  // Mute/unmute
  const setMuted = useCallback((value) => {
    audioManager.setMuted(value);
    setMutedState(value);
  }, []);

  // Stop all sounds
  const stopAll = useCallback(() => {
    audioManager.stopAll();
  }, []);

  const value = useMemo(() => ({
    playSound,
    playDice,
    setAmbience,
    setVolume,
    setMuted,
    stopAll,
    volumes,
    muted,
    initialized
  }), [playSound, playDice, setAmbience, setVolume, setMuted, stopAll, volumes, muted, initialized]);

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
}
